/**
 * Compact count formatter, kept behaviourally identical to the organizer profile page so the
 * same organizer reads the same on both surfaces.
 *
 * Source of truth:
 *   apps/organizer-profile-webapp/app/src/components/OrganizerPage/OrganizerInfo/Metrics/Metrics.tsx
 *
 * This is a deliberate copy rather than a shared import: `apps/listings-webapp` is excluded from
 * the pnpm workspace (see pnpm-workspace.yaml) and keeps its own npm trees, so it cannot depend on
 * a `packages/*` module today. Fold this into a shared package once listings-webapp joins the
 * workspace, and keep the two copies in sync until then.
 *
 * The trailing "+" marks a value that was rounded down, so we never overstate a count.
 */
export function formatCount(n: number, max?: number): string {
  if (!Number.isFinite(n) || n < 0) return '0';
  if (max != null && n > max) return `${formatCount(max)}+`;
  if (n >= 10_000_000) {
    const plus = n % 1_000_000 !== 0 ? '+' : '';
    return `${Math.floor(n / 1_000_000)}m${plus}`;
  }
  if (n >= 1_000_000) {
    const plus = n % 100_000 !== 0 ? '+' : '';
    return `${Math.floor(n / 100_000) / 10}m${plus}`;
  }
  if (n >= 10_000) {
    const plus = n % 1_000 !== 0 ? '+' : '';
    return `${Math.floor(n / 1_000)}k${plus}`;
  }
  if (n >= 1_000) {
    const plus = n % 100 !== 0 ? '+' : '';
    return `${Math.floor(n / 100) / 10}k${plus}`;
  }
  return String(Math.round(n));
}

/**
 * Upper bound for the organizer event count, mirroring MAX_DISPLAYED_EVENTS on the organizer
 * profile page. `eventsHosted` counts every series child occurrence, so uncapped values reach
 * the hundreds of thousands for recurring-event organizers and are not meaningful to a shopper.
 */
export const MAX_DISPLAYED_EVENTS = 3_000;
